import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, Treemap, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['hsl(188, 72%, 28%)', 'hsl(199, 89%, 48%)', 'hsl(36, 95%, 52%)', 'hsl(158, 64%, 39%)', 'hsl(2, 78%, 50%)'];

const Reportes = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.rol === 'SuperAdmin';
  const [stats, setStats] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [renta, setRenta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const requests = [
        axios.get(`${API}/stats`),
        axios.get(`${API}/lotes`),
        axios.get(`${API}/ofertas`)
      ];

      if (isSuperAdmin) {
        requests.push(axios.get(`${API}/stats/renta`).catch(() => ({ data: { renta: 0, totalAdjudicado: 0 } })));
      }

      const results = await Promise.all(requests);
      
      setStats(results[0].data);
      setLotes(results[1].data);
      setOfertas(results[2].data);
      
      if (isSuperAdmin && results[3]) {
        setRenta(results[3].data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  // Ofertas por día (dummy data - en producción usar datos reales)
  const ofertasPorDia = [
    { day: '10/10', offers: 3 },
    { day: '11/10', offers: 5 },
    { day: '12/10', offers: 4 },
    { day: '13/10', offers: 7 },
    { day: '14/10', offers: 6 },
  ];

  // Estados de publicaciones
  const estadosData = stats ? [
    { name: 'Activas', value: stats.publicaciones_activas || 0, fill: '#3b82f6' },
    { name: 'Vencidas', value: stats.publicaciones_vencidas || 0, fill: '#ef4444' },
    { name: 'En Espera', value: stats.publicaciones_en_espera || 0, fill: '#eab308' },
    { name: 'Adjudicadas', value: stats.lotes_adjudicados || 0, fill: '#10b981' },
    { name: 'No Adjudicadas', value: stats.publicaciones_no_adjudicadas || 0, fill: '#64748b' }
  ].sort((a, b) => b.value - a.value) : [];

  // Distribución por tipoGanado
  const normalizeBrand = (brand) => {
    if (!brand) return 'Desconocida';
    let cleaned = brand.trim().replace(/\s+/g, ' ').toLowerCase();
    
    if (['vw', 'volskwagen', 'vokswagen', 'volkswen'].includes(cleaned)) cleaned = 'volkswagen';
    if (cleaned === 'chevy') cleaned = 'chevrolet';
    if (['mb', 'mercedes', 'mercedes benz'].includes(cleaned)) cleaned = 'mercedes-benz';
    if (cleaned === 'bm') cleaned = 'bmw';
    if (cleaned === 'peugot') cleaned = 'peugeot';
    
    return cleaned.split(/[\s-]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const tipoGanadosCount = {};
  lotes.forEach((v) => {
    const tipoGanadoNormalizada = normalizeBrand(v.tipoGanado);
    tipoGanadosCount[tipoGanadoNormalizada] = (tipoGanadosCount[tipoGanadoNormalizada] || 0) + 1;
  });
  const tipoGanadosData = Object.entries(tipoGanadosCount).map(([name, value]) => ({ name, value }));

  // Comparación precio base vs adjudicado
  const preciosComparacion = lotes
    .filter((v) => v.estadoPublicacion === 'Adjudicada' && v.precioAdjudicado)
    .map((v) => ({
      nombre: `${v.tipoGanado} ${v.categoria}`,
      base: v.precioBase || 0,
      adjudicado: v.precioAdjudicado,
    }));

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-semibold tracking-tight">Reportes y Analítica</h1>
          <p className="text-muted-foreground mt-1">Visualización de datos y métricas</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Tabs defaultValue="general" className="space-y-6 bg-background min-h-screen">
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="ofertas">Ofertas</TabsTrigger>
            <TabsTrigger value="lotes">Lotes</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6 bg-background min-h-screen">
            <div className="grid gap-6 sm:grid-cols-2">
              {/* KPIs */}
              <Card>
                <CardHeader>
                  <CardTitle>Totales</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Publicaciones</div>
                      <div className="text-2xl font-bold">{stats?.total_publicaciones || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Ofertas</div>
                      <div className="text-2xl font-bold">{stats?.total_ofertas || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Adjudicados</div>
                      <div className="text-2xl font-bold">{stats?.lotes_adjudicados || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Tasa Adjudicación</div>
                      <div className="text-2xl font-bold">{stats?.tasa_adjudicacion || 0}%</div>
                    </div>
                    {isSuperAdmin && renta !== null && (
                      <div className="col-span-2 pt-2 border-t mt-1">
                        <div className="flex items-center justify-between">
                          <div className="text-muted-foreground font-medium">Renta Generada (0.5%)</div>
                          <div className="text-xl font-extrabold text-primary">
                            ${renta.renta.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Estados */}
              <Card>
                <CardHeader>
                  <CardTitle>Estados de Publicaciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="chart-status-bar">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={estadosData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value">
                          {estadosData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="ofertas" className="space-y-6 bg-background min-h-screen">
            <Card>
              <CardHeader>
                <CardTitle>Ofertas por Día</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80" data-testid="chart-offers-by-day">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ofertasPorDia}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="offers" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {preciosComparacion.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Precio Base vs Adjudicado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={preciosComparacion}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nombre" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="base" fill="hsl(var(--muted-foreground))" name="Precio Base" />
                        <Bar dataKey="adjudicado" fill="hsl(var(--primary))" name="Precio Adjudicado" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="lotes" className="space-y-6 bg-background min-h-screen">
            {tipoGanadosData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Distribución por TipoGanado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80" data-testid="chart-brand-treemap">
                    <ResponsiveContainer width="100%" height="100%">
                      <Treemap
                        data={tipoGanadosData}
                        dataKey="value"
                        aspectRatio={4 / 3}
                        stroke="#fff"
                        content={(props) => {
                          const { x, y, width, height, index, name, value } = props;
                          return (
                            <g>
                              <rect
                                x={x}
                                y={y}
                                width={width}
                                height={height}
                                style={{
                                  fill: COLORS[index % COLORS.length],
                                  stroke: '#fff',
                                  strokeWidth: 2,
                                }}
                              />
                              {width > 30 && height > 30 && (
                                <text x={x + width / 2} y={y + height / 2} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="bold">
                                  {name}
                                </text>
                              )}
                              {width > 30 && height > 45 && (
                                <text x={x + width / 2} y={y + height / 2 + 15} textAnchor="middle" fill="#fff" fontSize={10}>
                                  {value}
                                </text>
                              )}
                            </g>
                          );
                        }}
                      >
                        <Tooltip />
                      </Treemap>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Resumen de Lotes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Promedio Cantidad</div>
                      <div className="text-xl font-bold">
                        {lotes.length > 0
                          ? Math.round(lotes.reduce((sum, v) => sum + v.cantidad, 0) / lotes.length).toLocaleString()
                          : 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Lote Promedio</div>
                      <div className="text-xl font-bold">
                        {lotes.length > 0
                          ? Math.round(lotes.reduce((sum, v) => sum + v.numeroLote, 0) / lotes.length)
                          : 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Precio Promedio</div>
                      <div className="text-xl font-bold">
                        ${(() => {
                          const lotesConPrecio = lotes.filter(v => v.precioBase != null && v.precioBase > 0);
                          return lotesConPrecio.length > 0
                            ? Math.round(lotesConPrecio.reduce((sum, v) => sum + v.precioBase, 0) / lotesConPrecio.length).toLocaleString('en-US')
                            : 0;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Reportes;